// Secondary side panel mounted on the Calendar page. Contains the
// full Anträge / Reports / Meetings pages as tabs so the user can keep
// the calendar in view while triaging requests, reviewing reports or
// scheduling meetings.
//
// Layout:
//   • Desktop (>= lg): pushes the calendar smaller (row-flex sibling),
//     NOT an overlay.
//   • Mobile / tablet (< lg): rendert über die geteilte SidePanel-
//     Primitive — Bottom-Sheet am Phone (<sm, Portal, Drag-to-close,
//     Safe-Area unten verankert), Slide-in von rechts am Tablet. Die
//     Tab-Leiste sitzt im headerExtra-Slot und bleibt damit über dem
//     Scroll-Body fixiert und in der Standalone-PWA unter der Notch
//     hervor (D5).
//
// All three tab bodies stay mounted (after first activation) and only
// the active one is visible — this keeps each tab's filter / search /
// pagination state alive across tab switches within the same panel
// session. To keep the first open snappy, inactive tabs are lazy-mounted
// (only mounted once they have been visited at least once).

import { BarChart3, Inbox, Video, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useMediaQuery } from '../lib/use-media-query'
import { cn } from '../lib/utils'
import { AuswertungPageBody } from './auswertung-panel'
import { MeetingsPageBody } from './meetings-panel'
import { RequestsPageBody } from './requests-panel'
import { SidePanel } from './ui/side-panel'

export type CalendarInfoTab = 'requests' | 'reports' | 'meetings'

const TAB_STORAGE_KEY = 'calendar-info-tab'

function isTab(v: unknown): v is CalendarInfoTab {
  return v === 'requests' || v === 'reports' || v === 'meetings'
}

function loadInitialTab(): CalendarInfoTab {
  if (typeof window === 'undefined') return 'requests'
  try {
    const stored = window.localStorage.getItem(TAB_STORAGE_KEY)
    if (isTab(stored)) return stored
  } catch {
    // ignore — localStorage may be disabled
  }
  return 'requests'
}

interface CalendarInfoPanelProps {
  open: boolean
  onClose: () => void
  /** Additional class applied to the DESKTOP panel surface (sibling column
   * with fixed width). The <lg SidePanel ignores it — Breite/Position
   * kommen dort aus der Primitive. */
  className?: string
  /** When set, the panel scopes the "Einträge" list to THIS user (admin/
   * leader viewing another user's calendar). The Anträge/Meetings tabs can't
   * be user-scoped, so they are hidden in this mode. */
  targetUserId?: string
  /** Lesemodus — der eingebettete Einträge-Liste werden Bearbeiten/Löschen
   * ausgeblendet. */
  readOnly?: boolean
}

interface TabSpec {
  id: CalendarInfoTab
  label: string
  Icon: typeof Inbox
}

const TABS: TabSpec[] = [
  { id: 'requests', label: 'Anträge', Icon: Inbox },
  { id: 'reports', label: 'Einträge', Icon: BarChart3 },
  { id: 'meetings', label: 'Meetings', Icon: Video },
]

export function CalendarInfoPanel({
  open,
  onClose,
  className,
  targetUserId,
  readOnly = false,
}: CalendarInfoPanelProps) {
  // ≥lg: Push-Sibling neben dem Kalender (bewusst OHNE Esc-Close, das
  // Layout bleibt voll interaktiv). <lg: SidePanel-Overlay (Esc/Backdrop/
  // Drag-to-close übernimmt die Primitive).
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  // Viewing another user: only the entries list can be scoped to them, so we
  // lock the panel to the "Einträge" tab and hide Anträge/Meetings (which
  // would otherwise show the SESSION user's data).
  const scopedToOther = !!targetUserId
  const [storedActive, setActive] = useState<CalendarInfoTab>(() => loadInitialTab())
  const active: CalendarInfoTab = scopedToOther ? 'reports' : storedActive
  const visibleTabs = scopedToOther ? TABS.filter((t) => t.id === 'reports') : TABS
  // Track which tabs have ever been activated so we can lazy-mount
  // inactive bodies. The active tab is always included.
  const [mounted, setMounted] = useState<Set<CalendarInfoTab>>(
    () => new Set<CalendarInfoTab>([loadInitialTab()]),
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      // Persist the user's real choice, not the forced 'reports' override.
      window.localStorage.setItem(TAB_STORAGE_KEY, storedActive)
    } catch {
      // ignore
    }
  }, [storedActive])

  useEffect(() => {
    if (!mounted.has(active)) {
      setMounted((prev) => {
        const next = new Set(prev)
        next.add(active)
        return next
      })
    }
  }, [active, mounted])

  if (!open) return null

  const tabList = (
    <div role="tablist" aria-label="Info-Panel" className="flex items-center gap-0 flex-1">
      {visibleTabs.map(({ id, label, Icon }) => {
        const isActive = id === active
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => setActive(id)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
              isActive
                ? 'bg-accent/15 text-accent'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-overlay/40',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{label}</span>
          </button>
        )
      })}
    </div>
  )

  const tabBodies = (
    <>
      {/* Keep all visited tabs mounted but hide inactive ones via
          display:none so each tab keeps its scroll position, filter
          state and dirty form state across switches. */}
      {!scopedToOther && mounted.has('requests') && (
        <div className={active === 'requests' ? undefined : 'hidden'}>
          <RequestsPageBody embedded />
        </div>
      )}
      {(scopedToOther || mounted.has('reports')) && (
        <div className={active === 'reports' ? undefined : 'hidden'}>
          <AuswertungPageBody
            embedded
            initialTab="list"
            lockedTab
            hideTabs
            userId={targetUserId}
            readOnly={readOnly}
          />
        </div>
      )}
      {!scopedToOther && mounted.has('meetings') && (
        <div className={active === 'meetings' ? undefined : 'hidden'}>
          <MeetingsPageBody embedded />
        </div>
      )}
    </>
  )

  if (!isDesktop) {
    return (
      <SidePanel
        open={open}
        onClose={onClose}
        // Tablet-Slide-in braucht den Backdrop: ohne ihn landen Taps neben
        // dem Panel im Kalender-Grid (öffnete versehentlich Erfassen).
        desktopBackdrop
        title={TABS.find((t) => t.id === active)?.label ?? 'Liste'}
        // Tab-Leiste als fixierter Header-Zusatz: scrollt nicht mit dem
        // Body und übernimmt die untere Trennlinie des SidePanel-Headers.
        headerExtra={<div className="border-b border-border-subtle pb-1.5">{tabList}</div>}
      >
        {tabBodies}
      </SidePanel>
    )
  }

  return (
    <aside
      className={cn(
        'flex flex-col self-stretch',
        'rounded-xl border border-border-subtle bg-bg-elevated/40',
        'overflow-hidden',
        className,
      )}
      aria-label="Anträge, Reports und Meetings"
    >
      <header className="flex shrink-0 items-center gap-1 border-b border-border-subtle px-2 py-1.5">
        {tabList}
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="shrink-0 p-1.5 rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-overlay/60 touch:opacity-100"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3">{tabBodies}</div>
    </aside>
  )
}
